import { useNavigate } from "react-router-dom";
import { Flex, Badge, IconButton } from "@radix-ui/themes";
import {
  DotsVerticalIcon,
  Pencil1Icon,
  ArchiveIcon,
  ChevronRightIcon,
} from "@radix-ui/react-icons";
import { Kanban } from "@/v2/assets/icons/Kanban";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuIcon,
} from "@/components/ui/dropdown-menu";
import { Tracker } from "@/v2/entities/tracker/Tracker.model";
import { extractRulesForBadges } from "./TrackerUtils";
import styles from "../trackerDetail.module.scss";

interface TrackerHeaderProps {
  tracker: Tracker;
  onEdit: (e: React.MouseEvent) => void;
  onArchive: (e: React.MouseEvent) => void;
}

export function TrackerHeader({
  tracker,
  onEdit,
  onArchive,
}: TrackerHeaderProps) {
  const navigate = useNavigate();
  // Extract rule values for tooltip
  const rules = extractRulesForBadges(tracker.matchRules);
  // For tooltip: show full rule (field + value), as badge (like table)
  const ruleBadges = rules.map((rule: string, i: number) => (
    <Badge
      key={i}
      color="gray"
      variant="soft"
      size="1"
      style={{
        marginBottom: 2,
      }}
      title={rule}
    >
      {rule}
    </Badge>
  ));
  return (
    <div className={styles.trackerDetailHeader}>
      <IconButton
        variant="ghost"
        color="gray"
        size="2"
        onClick={() => navigate("/trackers")}
        aria-label="Back to trackers"
        className={styles.trackerDetailHeader__iconBtn}
      >
        <Kanban size={24} fill="gray" />
      </IconButton>
      <span className={styles.trackerDetailHeader__chevron}>
        <ChevronRightIcon />
      </span>
      <div className={styles.trackerDetailHeader__controls}>
        <HoverCard>
          <HoverCardTrigger asChild>
            <Flex align="center" gap="2" style={{ cursor: "pointer" }}>
              <Flex align="center" gap="2">
                <span className={styles.trackerDetailHeader__name}>
                  {tracker.name}
                </span>
                {tracker.sources && tracker.sources.length > 0 && (
                  <Flex align="center" gap="1">
                    {tracker.sources.includes("tickets") && (
                      <SimulatorChannelIcon
                        channel="autonomous_ticket_event"
                        size="1"
                      />
                    )}
                    {tracker.sources.includes("conversations") && (
                      <SimulatorChannelIcon
                        channel="autonomous_quackchat"
                        size="1"
                      />
                    )}
                  </Flex>
                )}
              </Flex>
            </Flex>
          </HoverCardTrigger>
          <HoverCardContent>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {tracker.name}
            </div>
            {tracker.sources && tracker.sources.length > 0 && (
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>
                <div style={{ marginBottom: 4 }}>Interaction sources</div>
                <Flex align="center" gap="2">
                  {tracker.sources.includes("tickets") && (
                    <Flex align="center" gap="1">
                      <SimulatorChannelIcon
                        channel="autonomous_ticket_event"
                        size="1"
                      />
                      <span>Tickets</span>
                    </Flex>
                  )}
                  {tracker.sources.includes("conversations") && (
                    <Flex align="center" gap="1">
                      <SimulatorChannelIcon
                        channel="autonomous_quackchat"
                        size="1"
                      />
                      <span>Conversations</span>
                    </Flex>
                  )}
                </Flex>
              </div>
            )}
            {ruleBadges.length > 0 && (
              <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>
                Capture rules
              </div>
            )}
            {ruleBadges.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {ruleBadges}
              </div>
            ) : (
              <span style={{ fontSize: 13, color: "#888" }}>
                All interactions
              </span>
            )}
          </HoverCardContent>
        </HoverCard>
        <Badge color={tracker.isArchived ? "gray" : "green"} variant="soft">
          {tracker.isArchived ? "Archived" : "Active"}
        </Badge>
        <span className={styles.trackerDetailHeader__menu}>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <IconButton variant="soft" color="gray" size="2">
                <DotsVerticalIcon width={16} height={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onEdit}>
                <DropdownMenuIcon>
                  <Pencil1Icon />
                </DropdownMenuIcon>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive}>
                <DropdownMenuIcon>
                  <ArchiveIcon />
                </DropdownMenuIcon>
                {tracker.isArchived ? "Unarchive" : "Archive"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>
    </div>
  );
}
